<script setup lang="ts">
/*
  The whole page of the Designer or the Operator while the server has no valid license.

  The server refuses both with `403 license_required` until a license key is entered and
  confirmed (or once 30 days pass without a confirmation); `useLicenseGate` heard it, from
  an API call or a room join. Someone who may manage the server is sent to the control
  center's License section, which stays open; anyone else is told whom to ask.

  "Try again" reloads the page: everything the views hold was refused, so asking again
  means loading again. With `hideActions` (a page that may not lead anywhere else) the
  control-center link is left out; "Try again" stays, as it leads nowhere.
*/
import { mdiCertificateOutline } from "@mdi/js";
import { HabButton, HabEmptyState } from "@livereader/habitus-vue";
import { useLicenseGate } from "@/shared/composables/useLicenseGate";
import { useSystemAccess } from "@/shared/composables/useSystemAccess";

defineProps<{ hideActions?: boolean }>();

const access = useSystemAccess();
const { clear } = useLicenseGate();

function tryAgain() {
	clear();
	window.location.reload();
}
</script>

<template>
	<div
		data-habitus
		class="flex flex-1 flex-col items-center justify-center gap-4 px-8 py-16"
	>
		<HabEmptyState
			:icon="mdiCertificateOutline"
			:title="$t('noLicense.title')"
			:note="
				access.can('system:manage_server')
					? $t('noLicense.noteAdmin')
					: $t('noLicense.note')
			"
		>
			<template #action>
				<div class="flex flex-wrap justify-center gap-2">
					<HabButton
						v-if="!hideActions && access.can('system:manage_server')"
						as="a"
						href="/control-center/license"
					>
						{{ $t("noLicense.openControlCenter") }}
					</HabButton>
					<HabButton
						variant="outlined"
						@click="tryAgain"
					>
						{{ $t("noLicense.tryAgain") }}
					</HabButton>
				</div>
			</template>
		</HabEmptyState>
	</div>
</template>
